import React from "react";
import {
  Card,
  Space,
  Typography,
  Select,
  Tag,
  Slider,
  Divider,
  Spin,
  Button,
} from "antd";
import {
  DollarOutlined,
  DashboardOutlined,
  ReloadOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ClockCircleOutlined,
  RightOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

/* -------------------- helpers -------------------- */
const fmtUSD  = (n) => (typeof n === "number" ? `$${n.toLocaleString()}` : "—");
const fmtMiles= (n) => (typeof n === "number" ? n.toLocaleString() : "—");
const fmtDist = (n) => (typeof n === "number" ? `${n.toFixed(1)} mi` : "—");

const MILEAGE_VARIANCES = [2, 5, 10, 15, 20];

/* -------------------- metric helpers -------------------- */
const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : undefined);
const cleanAvg = (vals) => {
  const nums = vals.filter((v) => typeof v === "number" && isFinite(v));
  return avg(nums);
};

/* -------------------- Metric card (dark, colored) -------------------- */
function MetricPro({ icon, label, value, unit, subTag, tone = "indigo", isMobile = false }) {
  const palette = {
    indigo: { chip: "rgba(99,102,241,.14)", ring: "rgba(99,102,241,.45)", fg: "#a5b4fc" },
    blue:   { chip: "rgba(59,130,246,.14)", ring: "rgba(59,130,246,.45)", fg: "#93c5fd" },
    amber:  { chip: "rgba(245,158,11,.14)", ring: "rgba(245,158,11,.45)", fg: "#fcd34d" },
    teal:   { chip: "rgba(45,212,191,.14)", ring: "rgba(45,212,191,.45)", fg: "#5eead4" },
    gray:   { chip: "rgba(148,163,184,.12)", ring: "rgba(148,163,184,.35)", fg: "#cbd5e1" },
  }[tone];

  // Mobile styles - much smaller and more compact to prevent horizontal scrolling
  if (isMobile) {
    // Format value for mobile (round mileage to thousands with 'k')
    const getFormattedValue = (label, value) => {
      if (label === "Avg Mileage" && value && value !== "—") {
        const numValue = typeof value === 'string' ? parseInt(value.replace(/[^\d]/g, '')) : value;
        if (numValue) {
          return `${Math.round(numValue / 1000)}k`;
        }
      }
      return value;
    };

    return (
      <div
        style={{
          borderRadius: 8,
          background: "linear-gradient(180deg, #0f1422 0%, #0b101a 100%)",
          border: "1px solid rgba(255,255,255,.06)",
          boxShadow: "0 2px 8px rgba(0,0,0,.2)",
          padding: 8,
          position: "relative",
          overflow: "hidden",
          minHeight: 60,
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          maxWidth: "100%",
        }}
      >
        {/* Glare effect */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 25%, transparent 50%)",
            borderRadius: 8,
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
        
        {/* Icon */}
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: palette.chip,
            boxShadow: `inset 0 0 0 1px ${palette.ring}`,
            flexShrink: 0,
          }}
        >
          <div style={{ color: palette.fg, fontSize: 12, lineHeight: 1 }}>{icon}</div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
          <div style={{ 
            color: palette.fg, 
            fontSize: 10, 
            fontWeight: 600, 
            textTransform: 'uppercase',
            letterSpacing: '0.3px',
            marginBottom: 4,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}>
            {label}
          </div>
          <div style={{ 
            fontSize: 16, 
            fontWeight: 800, 
            color: "#ffffff",
            lineHeight: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}>
            {getFormattedValue(label, value) ?? "—"}
          </div>
          {subTag}
        </div>
      </div>
    );
  }

  // Desktop styles - larger and more spacious
  return (
    <Card
      bordered={false}
      style={{
        borderRadius: 14,
        background: "linear-gradient(180deg, #0f1422 0%, #0b101a 100%)",
        border: "1px solid rgba(255,255,255,.06)",
        boxShadow: "0 10px 26px rgba(0,0,0,.35)",
        transition: "transform .15s ease, box-shadow .15s ease, border-color .15s",
        position: "relative",
        overflow: "hidden",
      }}
      bodyStyle={{ padding: 14, position: "relative", zIndex: 2 }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = palette.ring)}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,.06)")}
    >
      {/* Glare effect */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 25%, transparent 50%)",
          borderRadius: 14,
          pointerEvents: "none",
          zIndex: 1,
        }}
      />
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 12,
            display: "grid",
            placeItems: "center",
            background: palette.chip,
            boxShadow: `inset 0 0 0 1px ${palette.ring}`,
          }}
        >
          <div style={{ color: palette.fg, fontSize: 18, lineHeight: 1 }}>{icon}</div>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: "#94a3b8", fontSize: 12, fontWeight: 600 }}>{label}</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#e5f0ff" }}>{value ?? "—"}</div>
            {unit ? <div style={{ color: "#9aa6b2", fontWeight: 700 }}>{unit}</div> : null}
          </div>
          {subTag}
        </div>
      </div>
    </Card>
  );
}

/* -------------------- Main -------------------- */
export default function SoldCompsTab({ listingId, subjectMiles, listing, isMobile = false }) {
  // Use sold_listings directly from the listing object
  const comps = listing?.sold_listings || [];
  const loading = false; // No loading since it's from listing data
  const error = null;


  // slider bounds from data
  const allMiles = comps.map((c) => c.miles).filter((n) => typeof n === "number");
  const allDist  = comps.map((c) => c.dist).filter((n) => typeof n === "number");
  const milesMin = allMiles.length ? Math.min(...allMiles) : 0;
  const milesMax = allMiles.length ? Math.max(...allMiles) : 0;
  const distMin  = allDist.length ? Math.floor(Math.min(...allDist, 0)) : 0;
  const distMax  = allDist.length ? Math.ceil(Math.max(...allDist, 50)) : 50;

  const [sortKey, setSortKey] = React.useState("price");
  const [sortDir, setSortDir] = React.useState("asc");
  const [mileageRange, setMileageRange] = React.useState([milesMin, milesMax]);
  const [distanceRange, setDistanceRange] = React.useState([distMin, distMax]);
  const [usedSimilar, setUsedSimilar] = React.useState(false);
  const [similarMileagePct, setSimilarMileagePct] = React.useState(15);

  React.useEffect(() => {
    setMileageRange([milesMin, milesMax]);
    setDistanceRange([distMin, distMax]);
  }, [milesMin, milesMax, distMin, distMax]);

  // Apply default distance filter: "Within 100 Miles" (but not similar mileage)
  React.useEffect(() => {
    // Apply within 100 miles filter (default to 0-100)
    setDistanceRange([distMin, Math.min(distMax, 100)]);
  }, [distMin, distMax]);

  const toggleDir = () => setSortDir((d) => (d === "asc" ? "desc" : "asc"));
  const clearFilters = () => {
    setUsedSimilar(false);
    setMileageRange([milesMin, milesMax]);
    setDistanceRange([distMin, distMax]);
  };

  // Use subjectMiles from prop if available, fallback to first comp
  const baseMiles = typeof subjectMiles === "number" && isFinite(subjectMiles)
    ? subjectMiles
    : comps[0]?.miles || 0;

  const applySimilarMileageRange = (pct) => {
    const lo = Math.max(0, Math.floor(baseMiles * (1 - pct / 100)));
    const hi = Math.ceil(baseMiles * (1 + pct / 100));
    setMileageRange([lo, hi]);
    setUsedSimilar(true);
  };

  const useSimilarMileage = () => {
    if (usedSimilar) {
      setMileageRange([milesMin, milesMax]);
      setUsedSimilar(false);
    } else {
      applySimilarMileageRange(similarMileagePct);
    }
  };

  const handleMileagePctChange = (pct) => {
    setSimilarMileagePct(pct);
    if (usedSimilar) applySimilarMileageRange(pct);
  };

  // New: Filter within 100 miles button with toggle
  const useWithin100Miles = () => {
    if (distanceRange[1] === 100) {
      // If already active, turn it off - reset to full range
      setDistanceRange([distMin, distMax]);
    } else {
      // Apply within 100 miles filter
      setDistanceRange([distMin, Math.min(distMax, 100)]);
    }
  };

  /* filter + sort */
  const filtered = React.useMemo(() => {
    return comps.filter((c) => {
      const m = typeof c.miles === "number" ? c.miles : null;
      const d = typeof c.dist === "number" ? c.dist : null;
      
      // Check mileage filter (skip if null)
      const mileagePass = m == null || (m >= mileageRange[0] && m <= mileageRange[1]);
      // Check distance filter (skip if null)
      const distancePass = d == null || (d >= distanceRange[0] && d <= distanceRange[1]);
      
      return mileagePass && distancePass;
    });
  }, [comps, mileageRange, distanceRange]);

  const sorted = React.useMemo(() => {
    const arr = [...filtered];
    const factor = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      let va, vb;
      if (sortKey === "distance") {
        va = a.dist;
        vb = b.dist;
      } else if (sortKey === "price") {
        va = a.price;
        vb = b.price;
      } else if (sortKey === "sold_date") {
        va = a.last_seen_at_date ? new Date(a.last_seen_at_date).getTime() : null;
        vb = b.last_seen_at_date ? new Date(b.last_seen_at_date).getTime() : null;
      } else {
        va = a.miles;
        vb = b.miles;
      }
      
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const na = Number(va), nb = Number(vb);
      if (na === nb) return 0;
      return na > nb ? factor : -factor;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  // metrics from filtered set
  const count    = filtered.length;
  const avgPrice = cleanAvg(filtered.map((c) => c.price));
  const avgMiles = cleanAvg(filtered.map((c) => c.miles));
  const avgDom   = cleanAvg(filtered.map((c) => c.dom_active));
  const avgDist  = cleanAvg(filtered.map((c) => c.dist));

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {/* Summary strip */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile 
            ? "repeat(2, 1fr)" 
            : "repeat(2, 1fr)",
          gridTemplateRows: "repeat(2, auto)",
          gap: isMobile ? 6 : 12,
          marginTop: isMobile ? 8 : 12,
          maxWidth: "100%",
          overflow: "hidden",
          width: "100%",
        }}
      >
        <MetricPro
          tone="indigo"
          icon={<DollarOutlined />}
          label="Sold Listings"
          value={count}
          isMobile={isMobile}
        />
        <MetricPro
          tone="blue"
          icon={<DollarOutlined />}
          label="Average Price"
          value={avgPrice != null ? `$${Math.round(avgPrice).toLocaleString()}` : "—"}
          isMobile={isMobile}
        />
        <MetricPro
          tone="amber"
          icon={<DashboardOutlined />}
          label="Average Mileage"
          value={avgMiles != null ? Math.round(avgMiles).toLocaleString() : "—"}
          unit="mi"
          isMobile={isMobile}
        />
        <MetricPro
          tone="teal"
          icon={<ClockCircleOutlined />}
          label="Average DOM"
          value={avgDom != null && isFinite(avgDom) ? Math.round(avgDom) : "—"}
          unit="days"
          isMobile={isMobile}
        />
      </div>

      {/* Everything below tabs */}
      {loading ? (
        <div style={{ minHeight: 320, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* Controls */}
          <Card
            bordered={false}
            bodyStyle={{ padding: 12 }}
            style={{
              borderRadius: 12,
              background: "linear-gradient(180deg, #0f1422, #0b101a)",
              border: "1px solid rgba(255,255,255,.06)",
              boxShadow: "0 10px 26px rgba(0,0,0,.35)",
            }}
          >
            {/* Row A: Sort + View All Comps */}
            <div
              style={{
                display: "flex",
                gap: 12,
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Space size={8} wrap>
                <Text style={{ color: "#9aa6b2" }}>Sort by</Text>
                <Select
                  value={sortKey}
                  onChange={setSortKey}
                  style={{ width: 160, color: "#fff", background: "#181f3a" }}
                  dropdownStyle={{ background: "#181f3a" }}
                  options={[
                    { value: "distance", label: "Distance" },
                    { value: "price", label: "Price" },
                    { value: "miles", label: "Mileage" },
                    { value: "sold_date", label: "Sold Date" },
                  ]}
                  optionLabelProp="label"
                />
                <Button
                  onClick={toggleDir}
                  icon={sortDir === "asc" ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                >
                  {sortDir === "asc" ? "Asc" : "Desc"}
                </Button>
              </Space>

              <Button type="link" icon={<ReloadOutlined />} onClick={clearFilters}>
                View All Comps
              </Button>
            </div>

            <Divider style={{ margin: "10px 0", borderColor: "rgba(255,255,255,.08)" }} />

            {/* Row B: Sliders */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 20,
              }}
            >
              {/* Mileage */}
              <div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text strong style={{ color: "#e5f0ff" }}>Mileage</Text>
                  <Text style={{ color: "#8ea0c6" }}>
                    {mileageRange[0].toLocaleString()}–{mileageRange[1].toLocaleString()} mi
                  </Text>
                </div>
                <Slider
                  range
                  min={milesMin}
                  max={milesMax}
                  step={500}
                  value={mileageRange}
                  onChange={(v) => {
                    setMileageRange(v);
                    if (usedSimilar) setUsedSimilar(false);
                  }}
                  tooltip={{ formatter: (v) => `${v.toLocaleString()} mi` }}
                />
                <div style={{ display: "flex", justifyContent: "flex-start", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <div
                    className={`mileage-pill ${usedSimilar ? "mileage-pill-selected" : ""}`}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      minHeight: 24,
                      padding: "2px 4px 2px 11px",
                      borderRadius: 6,
                      border: "1px solid rgba(255,255,255,.12)",
                      background: usedSimilar ? "linear-gradient(90deg, #6366f1, #22c55e)" : "#181f3a",
                      color: "#fff",
                      fontSize: 14,
                      fontWeight: 600,
                      transition: "background 0.2s",
                    }}
                  >
                    <span onClick={useSimilarMileage} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                      Use similar mileage
                    </span>
                    <Select
                      value={similarMileagePct}
                      onChange={handleMileagePctChange}
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                      options={MILEAGE_VARIANCES.map((p) => ({ value: p, label: `±${p}%` }))}
                      style={{ width: 72, marginLeft: 6, color: "#fff" }}
                      className="mileage-pill-select"
                      dropdownStyle={{ background: "#181f3a" }}
                      dropdownClassName="mileage-pill-dropdown"
                    />
                  </div>
                </div>
              </div>

              {/* Distance */}
              <div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text strong style={{ color: "#e5f0ff" }}>Distance</Text>
                  <Text style={{ color: "#8ea0c6" }}>
                    {distanceRange[0].toFixed(0)}–{distanceRange[1].toFixed(0)} mi
                  </Text>
                </div>
                <Slider
                  range
                  min={distMin}
                  max={distMax}
                  step={1}
                  value={distanceRange}
                  onChange={setDistanceRange}
                  tooltip={{ formatter: (v) => `${v.toFixed(0)} mi` }}
                />
                <div style={{ display: "flex", justifyContent: "flex-start", gap: 8 }}>
                  <Button
                    size="small"
                    type={distanceRange[1] === 100 ? "primary" : "default"}
                    onClick={useWithin100Miles}
                    className={distanceRange[1] === 100 ? "within-100-selected filter-btn" : "filter-btn"}
                  >
                    Within 100 miles
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          {/* List */}
          {error && (
            <div style={{ color: "#f87171", textAlign: "center", margin: "32px 0" }}>
              Error loading sold comparisons.
            </div>
          )}
          {!error && sorted.length === 0 && (
            <div style={{ 
              color: "#9ca3af", 
              textAlign: "center", 
              margin: "32px 0",
              padding: "40px 20px",
              background: "linear-gradient(180deg, #0f1422, #0b101a)",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,.06)",
            }}>
              <div style={{ fontSize: 16, marginBottom: 8, fontWeight: 600 }}>No Sold Listings Found</div>
              <div style={{ fontSize: 14 }}>
                {comps.length === 0 
                  ? "No sold comparisons available for this vehicle"
                  : "Try adjusting your filters to see more results"}
              </div>
            </div>
          )}
          {!error && sorted.map((c) => (
            <Card
              key={c.vin || c.id}
              bordered
              style={{
                borderRadius: 14,
                background: "linear-gradient(180deg, #0f1422, #0b101a)",
                borderColor: "rgba(255,255,255,.06)",
                boxShadow: "0 10px 26px rgba(0,0,0,.35)",
              }}
              bodyStyle={{ padding: 12 }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "120px 1fr auto",
                  gap: 14,
                  alignItems: "center",
                }}
              >
                {/* Photo */}
                <div
                  style={{
                    width: 120,
                    height: 80,
                    borderRadius: 10,
                    overflow: "hidden",
                    background: "#0b1220",
                    display: "grid",
                    placeItems: "center",
                    boxShadow: "inset 0 0 0 1px rgba(255,255,255,.06)",
                  }}
                >
                  {c.media?.photo_links_cached?.[0] ? (
                    <img
                      src={c.media.photo_links_cached[0]}
                      alt={c.heading || `${c.year} ${c.make} ${c.model}`}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <Text style={{ color: "#8ea0c6" }}>No photo</Text>
                  )}
                </div>

                {/* Details */}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: "#e5f0ff" }}>
                    {c.heading || `${c.year} ${c.make} ${c.model} ${c.trim}`}
                  </div>
                  <div style={{ marginTop: 2, color: "#dbe6ff", display: "flex", alignItems: "center", gap: 16 }}>
                    <Text>{fmtUSD(c.price)}</Text>
                    <Text>{fmtMiles(c.miles)} mi</Text>
                    {c.dom_active && (
                      <Tag 
                        style={{ 
                          marginLeft: 0,
                          fontSize: 10,
                          padding: "2px 8px",
                          background: "rgba(147, 51, 234, 0.1)",
                          border: "1px solid rgba(147, 51, 234, 0.3)",
                          color: "#a78bfa",
                        }}
                      >
                        {c.dom_active} days
                      </Tag>
                    )}
                  </div>
                  <div style={{ color: "#8ea0c6", marginTop: 4, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span>{c.dealer?.name || c.dealer_name}</span>
                    {typeof c.dist === "number" && (
                      <span style={{ color: "#93c5fd" }}>
                        · {fmtDist(c.dist)}
                      </span>
                    )}
                    {c.last_seen_at_date && (
                      <Tag 
                        color="green" 
                        style={{ 
                          marginLeft: 0,
                          fontSize: 10,
                          padding: "2px 8px",
                          background: "rgba(34, 197, 94, 0.1)",
                          border: "1px solid rgba(34, 197, 94, 0.3)",
                          color: "#22c55e",
                        }}
                      >
                        Sold {c.last_seen_at_date ? new Date(c.last_seen_at_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                      </Tag>
                    )}
                  </div>
                </div>

                {/* CTA */}
                <a href={c.vdp_url} target="_blank" rel="noopener noreferrer">
                  <RightOutlined style={{ color: "#9ca3af" }} />
                </a>
              </div>
            </Card>
          ))}
        </>
      )}

      <style>{`
        .ant-select-selector {
          color: #fff !important;
          background: #181f3a !important;
        }
        .ant-select-dropdown {
          background: #181f3a !important;
        }
        .ant-select-item-option-content {
          color: #fff !important;
        }
        .ant-btn[type="link"], .ant-btn:not(.filter-btn) {
          box-shadow: none !important;
          background: transparent !important;
          color: #fff !important; /* Always white text */
          border: 1px solid #8b5cf6 !important;
          transition: border-color 0.2s;
        }
        .ant-btn[type="link"]:active, .ant-btn:not(.filter-btn):active,
        .ant-btn[type="link"]:focus, .ant-btn:not(.filter-btn):focus,
        .ant-btn[type="link"]:hover, .ant-btn:not(.filter-btn):hover {
          background: transparent !important;
          color: #fff !important; /* Always white text */
          box-shadow: none !important;
          border-color: #8b5cf6 !important; /* Purple outline */
          filter: none !important;
          outline: none !important;
        }
        .filter-btn {
          transition: background 0.2s, color 0.2s;
        }
        .similar-mileage-selected.filter-btn,
        .within-100-selected.filter-btn {
          background: linear-gradient(90deg, #6366f1, #22c55e) !important;
          color: #fff !important;
          border: none !important;
          box-shadow: 0 2px 8px rgba(99,102,241,0.10);
          font-weight: 700;
        }
        .filter-btn:active,
        .similar-mileage-selected.filter-btn:active,
        .within-100-selected.filter-btn:active {
          background: linear-gradient(90deg, #6366f1, #22c55e) !important;
          color: #fff !important;
          filter: none !important;
          box-shadow: 0 2px 8px rgba(99,102,241,0.10);
        }
        /* Only highlight filter buttons, not sort or reset */
        .ant-btn-primary:not(.filter-btn) {
          background: #1677ff !important;
          color: #fff !important;
          border: none !important;
          box-shadow: none !important;
          font-weight: 700;
        }
      `}</style>
    </div>
  );
}